// src/features/battle/BattlePage.js
import { useEffect, useState } from 'react';
import CharacterCard from '../../components/CharacterCard';
import InventoryBar from '../../components/InventoryBar';
import ShopModal from '../../components/ShopModal';
import StoryBanner from '../../components/StoryBanner';
import { enemyStatsForWave, grantRewards, loadProgress, saveProgress } from '../game/progress';
import { saveSlot } from '../game/saveSlots';
import StatsModal from '../../components/StatsModal';
import StatUpgradeModal from '../../components/StatUpgradeModal';
import { deleteSlot } from '../game/saveSlots';

export default function BattlePage({ slotIndex, hero, setHero, onExit }) {
  // core state
  const [enemy, setEnemy] = useState(null);
  const [turn, setTurn] = useState('player');
  const [log, setLog] = useState([]);
  const [over, setOver] = useState(false);

  // anim flags
  const [pAtk, setPAtk] = useState(false);
  const [pHit, setPHit] = useState(false);
  const [eAtk, setEAtk] = useState(false);
  const [eHit, setEHit] = useState(false);

  // hp
  const [php, setPhp] = useState(hero.hp ?? 100);
  const [ehp, setEhp] = useState(100);

  // meta & ui
  const [progress, setProgress] = useState(loadProgress());
  const [openShop, setOpenShop] = useState(false);
  const [showStory, setShowStory] = useState(true);
  const [openStats, setOpenStats] = useState(false);
  const [openUpgrade, setOpenUpgrade] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [dead, setDead] = useState(false);

  // floating dmg
  const [dmgPlayer, setDmgPlayer] = useState(null);
  const [dmgEnemy, setDmgEnemy] = useState(null);

  // 1-turn buff
  const [pBuff, setPBuff] = useState({ mult: 1, turns: 0, name: '' });
  const getBuffMult = () => (pBuff.turns > 0 ? pBuff.mult : 1);
  useEffect(() => {
    if (turn === 'player' && pBuff.turns > 0) {
      setPBuff((b) => ({ ...b, turns: Math.max(0, b.turns - 1) }));
    }
  }, [turn]);

  // autosave
  useEffect(() => { saveProgress(progress); saveSlot(slotIndex, { ...hero, hp: php }, progress); }, [progress]);
  useEffect(() => { saveSlot(slotIndex, { ...hero, hp: php }, progress); }, [hero, php]);

  // spawn enemy by wave
  useEffect(() => {
  const pools = {
    0: ['Goblin','Slime','Kobold','Bandit'],
    1: ['Orc','Wraith','Hobgoblin','Harpy'],
    2: ['Troll','Golem','Specter','Vampire Thrall'],
    3: ['Minotaur','Wyvern','Lizardman','Mimic'],
  };

  const { hpMult, strBase, tier } = enemyStatsForWave(progress.wave);
  const pool = pools[Math.min(tier, 3)] || pools[0];

  // ใส่โอกาสเจอบอสทุก ๆ 5 เวฟ
  const bosses = ['Ogre Chief','Bone Dragon','Arch Wraith','Dread Knight'];
  const isBoss = progress.wave % 5 === 0;
  const baseName = isBoss
    ? bosses[(tier) % bosses.length]
    : pool[Math.floor(Math.random() * pool.length)];

  const hpMax = Math.round((isBoss ? 180 : 110 + Math.random()*30) * hpMult * (isBoss ? 1.2 : 1));
  const str = Math.round((isBoss ? strBase + 4 : strBase) + Math.random() * (isBoss ? 6 : 4));

  setEnemy({
    name: tier>0 ? `${baseName} [Tier ${tier}]` : baseName,
    hpMax,
    str,
    img: `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(baseName)}`
  });

  setEhp(hpMax);
  setPhp(hero.hp ?? 100);
  setTurn('player');
  setOver(false);
  setLog([`— Wave ${progress.wave} —`]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [progress.wave]);


  // helpers
  const pushLog = (s) => setLog((l) => [s, ...l].slice(0, 80));
  const roll = (n) => Math.floor(Math.random()*n);
  const calcDmg = (atk,m=1)=>Math.max(1,Math.round(atk*m+roll(6)));
  const resolve = (base) => { const crit = Math.random() < 0.15; return { amt: Math.round(base*(crit?1.5:1)), crit }; };
  const floatEnemy = (n)=>{ setDmgEnemy(n); setTimeout(()=>setDmgEnemy(null),800); };
  const floatPlayer = (n)=>{ setDmgPlayer(n); setTimeout(()=>setDmgPlayer(null),800); };

  // actions — player
  const attack = () => {
    if (over || turn!=='player' || !enemy) return;
    setPAtk(true); setTimeout(()=>setPAtk(false),260);
    const baseAtk = (hero.str||5)+(hero.weapon?.base||0);
    const pre = calcDmg(baseAtk,1.0)*getBuffMult();
    const { amt, crit } = resolve(pre);
    floatEnemy(amt); setEHit(true); setTimeout(()=>setEHit(false),180);
    setEhp(v=>{
      const nv=Math.max(0,v-amt);
      pushLog(`${hero.name} deals ${amt}${crit?' (CRIT!)':''} damage`);
      if(nv<=0) onWin(); else setTurn('enemy');
      return nv;
    });
  };

  const skill = () => {
    if (over || turn!=='player' || !enemy) return;
    setPAtk(true); setTimeout(()=>setPAtk(false),260);

    const s = skillPower(hero);
    if (s.kind==='buff') {
      setPBuff({ mult:1.25, turns:1, name:hero.skill?.name });
      pushLog(`${hero.name} uses ${hero.skill?.name} (Next turn x1.25 dmg)`);
      setTurn('enemy'); return;
    }
    if (s.kind==='heal') {
      setPhp(v=>Math.min(100,v+s.amount));
      pushLog(`${hero.name} heals +${s.amount}`);
      setTurn('enemy'); return;
    }
    const pre = s.amount*getBuffMult();
    const { amt, crit } = resolve(pre);
    floatEnemy(amt); setEHit(true); setTimeout(()=>setEHit(false),180);
    setEhp(v=>{
      const nv=Math.max(0,v-amt);
      pushLog(`${hero.name} casts ${hero.skill?.name} for ${amt}${crit?' (CRIT!)':''}`);
      if(nv<=0) onWin(); else setTurn('enemy');
      return nv;
    });
  };

  const heal = ()=>{ if(over||turn!=='player')return;
    const h=Math.min(22,Math.round((hero.int||5)+10));
    setPhp(v=>Math.min(100,v+h));
    pushLog(`${hero.name} heals ${h}`); setTurn('enemy');
  };

  const usePotion = ()=>{ // ใช้โพชั่น 1 ขวดจากคลัง (ถ้ามี)
    const idx = (progress.inv||[]).findIndex(i=>i.type==='potion');
    if (idx<0 || over || turn!=='player') return;
    const inv = [...progress.inv];
    const item = inv[idx];
    inv.splice(idx,1);
    const healVal = item.id==='potion_l'?60:25;
    setPhp(v=>Math.min(100, v+healVal));
    setProgress({...progress, inv});
    pushLog(`${hero.name} drinks ${item.name} (+${healVal})`);
    setTurn('enemy');
  };

  // actions — enemy
  useEffect(()=>{
    if(turn!=='enemy'||over||!enemy)return;
    const t=setTimeout(()=>{
      const useSkill=Math.random()<0.35;
      setEAtk(true);setTimeout(()=>setEAtk(false),260);
      setPHit(true);setTimeout(()=>setPHit(false),180);
      const base=calcDmg(enemy.str,useSkill?1.2:1.0);
      const {amt,crit}=resolve(base);
      floatPlayer(amt);
      setPhp(v=>{
        const nv=Math.max(0,v-amt);
        pushLog(`${enemy.name} hits you for ${amt}${crit?' (CRIT!)':''}`);
        if(nv<=0)onLose();else setTurn('player');
        return nv;
      });
    },650);
    return()=>clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[turn,over,enemy]);

  // results
  const onWin=()=>{ setOver(true); pushLog('🎉 Victory!');
    const gold=15+Math.round(progress.wave*5);
    const xp=25+Math.round(progress.wave*8);
    setProgress(p=>grantRewards(p,{gold,xp})); setOpenShop(true);
  };
  const onLose=()=>{ setOver(true); pushLog('💀 Defeat at wave '+progress.wave); setDead(true); };
  const continueNext=()=>{ setOpenShop(false); setProgress(p=>({...p,wave:p.wave+1})); setShowStory(true); setOpenUpgrade(true); };

  // ======= RENDER: 2-column layout (Player+Commands | Enemy+Log) =======
  return (
    <div className="page battle">
      <InventoryBar progress={progress}/>
      <header className="hud">
        <h1 className="title">Hero Forge Arena</h1>
        <div className="badges">
          <span className={`badge ${turn==='player'?'glow':''}`}>Your Turn</span>
          <span className={`badge ${turn==='enemy'?'glow-danger':''}`}>Enemy Turn</span>
          {pBuff.turns>0 && <span className="badge glow">Buffed x{pBuff.mult.toFixed(2)}</span>}
        </div>
      </header>

      {/* Two-column grid */}
      <div className="battle-grid">
        {/* Left: Player + Commands */}
        <section className="col-left">
          <div className="sticky-card">
            <div className="side-wrap">
              {dmgPlayer!=null && <div className="dmg-float player">-{dmgPlayer} Damage</div>}
              <CharacterCard
                side="player"
                name={hero.name}
                cls={hero.cls}
                hp={php}
                maxHp={100}
                img={`https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(hero.name)}`}
                weapon={hero.weapon}
                isAttacking={pAtk}
                isHit={pHit}
                auraColor="#60a5fa"
              />
            </div>
          </div>

          <div className="card command">
            <div className="buttons vertical">
              <button className="btn primary" onClick={attack} disabled={over||turn!=='player'}>Attack</button>
              <button className="btn alt" onClick={skill} disabled={over||turn!=='player'}>
                {hero.skill?.name ? `Skill: ${hero.skill.name}` : 'Skill'}
              </button>
              <button className="btn ghost" onClick={heal} disabled={over||turn!=='player'}>Heal</button>
              <button className="btn" onClick={usePotion} disabled={!progress.inv?.some(i=>i.type==='potion')}>Use Potion</button>
              <button className="btn success" onClick={()=>setOpenStats(true)}>View Stats</button>
              <button className="btn danger" onClick={()=>setConfirmExit(true)}>Exit to Saves</button>
            </div>
          </div>
        </section>

        {/* Right: Enemy + Log */}
        <section className="col-right">
          <div className="sticky-card">
            {enemy && (
              <div className="side-wrap">
                {dmgEnemy!=null && <div className="dmg-float enemy">-{dmgEnemy} Damage</div>}
                <CharacterCard
                  side="enemy"
                  name={enemy.name}
                  cls="Monster"
                  hp={ehp}
                  maxHp={enemy.hpMax}
                  img={enemy.img}
                  weapon={{name:'Claws',base:4}}
                  isAttacking={eAtk}
                  isHit={eHit}
                  auraColor="#f87171"
                />
              </div>
            )}
          </div>

          <div className="log card">
            <h3>Battle Log</h3>
            <ul>{log.map((l,i)=><li key={i}>{l}</li>)}</ul>
          </div>
        </section>
      </div>

      {/* Modals */}
      <ShopModal open={openShop} onClose={continueNext}
        progress={progress} setProgress={setProgress}
        hero={hero} setHero={setHero}/>
      <StoryBanner wave={showStory?progress.wave:null} onClose={()=>setShowStory(false)}/>
      <StatsModal open={openStats} hero={hero} onClose={()=>setOpenStats(false)}/>
      <StatUpgradeModal open={openUpgrade} points={2}
        hero={hero} setHero={setHero} slotIndex={slotIndex}
        onClose={()=>setOpenUpgrade(false)}/>

      {/* Confirm Exit */}
      {confirmExit && (
        <div className="modal">
          <div className="modal-card">
            <h2>Confirm</h2>
            <p>กลับไปหน้าบันทึกเซฟ (Exit to Saves) ใช่ไหม?</p>
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn danger" onClick={onExit}>Yes, Exit</button>
              <button className="btn" onClick={() => setConfirmExit(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Game Over */}
      {dead && (
        <div className="death-overlay">
          <div className="death-text">YOU ARE DEAD</div>
          <div className="modal-card death-card">
            <p>ลบเซฟช่องนี้แล้วกลับหน้าโหลดเซฟไหม?</p>
            <div className="row" style={{ marginTop: 10 }}>
              <button
                className="btn danger"
                onClick={() => {
                  try { deleteSlot(slotIndex); } catch {}
                  onExit();
                }}
              >
                Delete This Save & Return
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// skill power (INT scaling + kind)
function skillPower(hero){
  const INT=hero.int||5;
  const skill=hero.skill;
  if(!skill)return{kind:'damage',amount:Math.round(INT*1.0+6)};
  const base=skill.base??6;
  if(skill.kind==='damage')return{kind:'damage',amount:Math.round(base+INT*0.8)};
  if(skill.kind==='heal')return{kind:'heal',amount:Math.round(base+INT*0.7)};
  return{kind:'buff',amount:0};
}
